import gettext from '@sdk/lang/gettext';
import validateNumber, {
  ValidateOptions,
  ValidNumberOption,
} from '@sdk/utils/validation/validateNumber';
import { AutomatedBreaksApiFields } from '@sdk/authentication/types/apiTypes';

import { BREAKS_VALIDATION_FIELD_NAMES } from '@web/settings/components/schedule/Breaks/breaks.consts';
import {
  AllowedBreaksErrors,
  BreaksSettings,
} from '@web/settings/components/schedule/Breaks/breaks.types';

export const minutesValidator = (value: string) => {
  if (
    !validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
      [ValidateOptions.integer]: true,
      [ValidateOptions.validNumberLowerOrEqualThan]: 60,
    })
  ) {
    return gettext('Enter a valid value');
  }
};

/**
 * Validates if input break threshold  number represent hours in a day (1 - 24)
 */
export const hoursValidator = (value: string) => {
  if (
    !validateNumber(value, {
      [ValidateOptions.validNumber]: ValidNumberOption.positive,
      [ValidateOptions.validNumberGreaterThan]: 0,
      [ValidateOptions.validNumberLowerOrEqualThan]: 24,
    })
  ) {
    return gettext('Enter a valid value');
  }
};

/**
 * Validates if break duration is less than the break threshold
 *
 * @param breakDuration number
 * @param breakThresholdInMinutes number
 * @return
 */
export const breakDurationLessThanBreakThresholdValidator = (
  breakDuration: number,
  breakThresholdInMinutes: number
) => {
  if (breakDuration > breakThresholdInMinutes) {
    return gettext('Enter a valid value');
  }
};

/**
 * Validates if the break threshold is greater than the break duration
 *
 * @param breakDuration number
 * @param breakThresholdInMinutes number
 * @return
 */
export const breakThresholdGreaterThanBreakDurationValidator = (
  breakThresholdInMinutes: number,
  breakDuration: number
) => {
  if (breakThresholdInMinutes < breakDuration) {
    return gettext('Enter a valid value');
  }
};

/**
 * Validates if number is multiple of 0.5
 *
 * @param value number
 * @return
 */
export const validateIsNumberMultipleOfHalfHour = (value: number) => {
  if (value % 0.5 !== 0) {
    return gettext('Decimals can only include a half hour value');
  }
};

/**
 * Validation function that allows empty strings and numbers with at most one decimal place
 *
 * @param str string
 * @return boolean
 */
export function hasAtMostOneDecimalPlace(str) {
  const regex = /^(\d+(\.\d{0,1})?)?$/;
  return regex.test(str);
}

export const getAutomatedBreaksFieldNameErrors = (tabErrors: string[]) =>
  BREAKS_VALIDATION_FIELD_NAMES.filter((fieldName) => tabErrors.includes(fieldName));

export const getInitialBreaksSettingsFromInvalidFields = (
  errors: AllowedBreaksErrors[],
  breaksInitialValues: BreaksSettings
) =>
  errors.reduce((acc, error) => {
    if (error === AutomatedBreaksApiFields.automatedBreaksIsEnabled) {
      acc.automatedBreaksMealIsEnabled = breaksInitialValues.automatedBreaksMealIsEnabled;
      acc.automatedBreaksRestIsEnabled = breaksInitialValues.automatedBreaksRestIsEnabled;
    } else {
      acc[error] = breaksInitialValues[error];
    }

    return acc;
  }, {} as BreaksSettings);

export const hoursThatMapToWholeNumberOfMinutesValidators = ({
  minValue = 0,
  maxValue = 24,
  skipValidation = false,
}) => [
  (value) => {
    if (skipValidation) {
      return;
    }

    if (
      !value ||
      !validateNumber(value, {
        [ValidateOptions.validNumber]: ValidNumberOption.positive,
      })
    ) {
      return gettext('Enter a valid value');
    }
  },
  (value) => validateIsNumberMultipleOfHalfHour(Number(value)),
  (value) => {
    if (skipValidation) {
      return;
    }

    return validateIsNumberMultipleOfHalfHour(Number(value));
  },
  (value) => {
    if (skipValidation) {
      return;
    }

    if (Number(value) > maxValue) {
      return gettext('Cannot exceed [%(maxValue)s] hours', { maxValue });
    }
  },
  (value) => {
    if (skipValidation) {
      return;
    }

    if (Number(value) < minValue) {
      return gettext('Cannot be less than %(minValue)s hours', { minValue });
    }
  },
];
